// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { RefreshCw } from 'lucide-react';

import { Dash } from '@/components/greenlight/Dash';
import { LiveIndicator } from '@/components/greenlight/LiveIndicator';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { UpdateRunPhase, UpdateRunTranscriptMeta } from '@greenlight/shared';

import { LogDrawer, type LogPage } from './LogDrawer';
import {
  byteLabel,
  durationLabel,
  emptyTranscriptCopy,
  modeLabel,
  phaseLabel,
  phaseStatusLabel,
  reasonLabel,
  runOutcomeMeta,
} from './updaterRunMeta';
import type { UpdaterRunSource, UpdaterRunView } from './updaterRunSource';

const PHASE_VARIANT: Partial<
  Record<UpdateRunPhase['status'], 'active' | 'failed' | 'deploying' | 'inactive'>
> = {
  succeeded: 'active',
  failed: 'failed',
  started: 'deploying',
  skipped: 'inactive',
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium text-foreground">{children}</dd>
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <h3 className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </h3>
  );
}

/**
 * Per-phase status, duration, and the bounded reason the phase recorded. A
 * phase still `started` on a run that has an outcome was cut off by its Job,
 * so it reads as interrupted rather than running.
 */
function PhaseTimeline({
  phases,
  settled,
}: {
  phases: UpdaterRunView['phases'];
  settled: boolean;
}): React.JSX.Element {
  if (phases.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This run recorded no phases — it never reached its first step, or never reached the database
        at all.
      </p>
    );
  }

  return (
    <ol className="space-y-px overflow-hidden rounded-[8px] border border-border bg-border">
      {phases.map((phase) => {
        const interrupted = settled && phase.status === 'started';
        return (
          <li key={phase.phase} className="flex flex-wrap items-center gap-2 bg-card px-3 py-2.5">
            <StatusBadge
              variant={interrupted ? 'failed' : (PHASE_VARIANT[phase.status] ?? 'inactive')}
            >
              {interrupted ? 'Interrupted' : phaseStatusLabel(phase.status)}
            </StatusBadge>
            <span className="text-sm font-medium capitalize text-foreground">
              {phaseLabel(phase.phase)}
            </span>
            <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
              {durationLabel(phase.duration_ms) ?? (interrupted ? null : 'still running')}
            </span>
            {(phase.reason !== null || phase.details != null) && (
              <dl className="w-full space-y-0.5 pt-1 text-[11px]">
                {phase.reason !== null && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground">reason</dt>
                    <dd className="font-medium text-foreground">{reasonLabel(phase.reason)}</dd>
                  </div>
                )}
                {phase.details != null &&
                  Object.entries(phase.details).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <dt className="font-mono text-muted-foreground">{key}</dt>
                      <dd className="min-w-0 break-all font-mono text-foreground">
                        {String(value)}
                      </dd>
                    </div>
                  ))}
              </dl>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The exact non-secret inputs the run replayed — the install-input snapshot the
 * installer persists, plus the flags the updater adds itself. Secret-free by
 * construction upstream: the installer refuses to record a value registered as
 * a secret rather than filtering it later.
 */
function Parameters({
  parameters,
}: {
  parameters: Readonly<Record<string, string>> | null;
}): React.JSX.Element {
  const entries = Object.entries(parameters ?? {});
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No parameters were recorded. A run that never got as far as reading the install-input
        snapshot has none, and a synthesized run never had any.
      </p>
    );
  }

  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-[12px]">
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt className="font-mono text-muted-foreground">{key}</dt>
          <dd className="break-words font-mono text-foreground">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/**
 * An open run's phases arrive while the drawer is up, so it says that it is
 * following them and offers the manual read anyway: a panel that silently
 * repolls is indistinguishable from a stuck one, and waiting out an interval
 * to find out is the thing an operator mid-update has least patience for.
 * A settled run is immutable, so neither control appears on one.
 */
function LiveHeader({
  isFetching,
  onRefresh,
}: {
  isFetching: boolean;
  onRefresh: () => void;
}): React.JSX.Element {
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-gl border border-border bg-muted/40 px-3 py-2"
      data-testid="updater-run-live"
    >
      {/* What the phases do is already the outcome line's sentence directly
          above; this says only that the panel is watching for them. */}
      <LiveIndicator label="Following this run" />
      <Button
        variant="ghost"
        size="sm"
        className="ml-auto h-11 shrink-0 gap-1.5 text-muted-foreground md:h-8"
        disabled={isFetching}
        data-testid="updater-run-refresh"
        onClick={onRefresh}
      >
        <RefreshCw aria-hidden="true" className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
        {isFetching ? 'Refreshing…' : 'Refresh'}
      </Button>
    </div>
  );
}

function DetailBody({
  run,
  isFetching,
  onRefresh,
}: {
  run: UpdaterRunView;
  isFetching: boolean;
  onRefresh: () => void;
}): React.JSX.Element {
  const meta = runOutcomeMeta(run);
  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
          <span className="text-[11px] text-muted-foreground">
            {modeLabel(run.mode)} · {run.trigger}-triggered · started {relativeTime(run.started_at)}
          </span>
        </div>
        <p className="mt-2 text-sm text-foreground">{meta.meaning}</p>
      </div>

      {run.outcome === null && <LiveHeader isFetching={isFetching} onRefresh={onRefresh} />}

      <dl>
        <Field label="Version">
          {run.previous_version !== null || run.target_version !== null ? (
            <span className="inline-flex items-center gap-1.5">
              <MonoValue>{run.previous_version ?? 'unknown'}</MonoValue>
              <span aria-hidden="true" className="text-muted-foreground">
                &rarr;
              </span>
              <MonoValue>{run.target_version ?? 'unknown'}</MonoValue>
            </span>
          ) : (
            <Dash />
          )}
        </Field>
        <Field label="Duration">{durationLabel(run.duration_ms) ?? 'still running'}</Field>
        {run.reason !== null && <Field label="Reason">{reasonLabel(run.reason)}</Field>}
        {run.manifest_sha256 !== undefined && (
          <Field label="Manifest digest">
            {run.manifest_sha256 !== null ? (
              <MonoValue copyable className="break-all">
                {run.manifest_sha256}
              </MonoValue>
            ) : (
              <Dash />
            )}
          </Field>
        )}
        {run.job_name !== undefined && (
          <Field label="Kubernetes Job">
            {run.job_name !== null ? (
              <MonoValue className="break-all">{run.job_name}</MonoValue>
            ) : (
              <Dash />
            )}
          </Field>
        )}
      </dl>

      <section className="space-y-2">
        <Heading>Phases</Heading>
        <PhaseTimeline phases={run.phases} settled={run.outcome !== null} />
      </section>

      {run.parameters !== undefined && (
        <section className="space-y-2">
          <Heading>Parameters replayed</Heading>
          <Parameters parameters={run.parameters} />
        </section>
      )}
    </div>
  );
}

/** One line naming what the transcript viewer is showing before a byte of it is read. */
function transcriptCaption(
  transcript: UpdateRunTranscriptMeta,
  isPreview: boolean,
  truncatedCopy = 'trimmed to its last portion at the 2 MB cap',
): string {
  return [
    `${byteLabel(transcript.size_bytes)} captured`,
    ...(transcript.truncated ? [truncatedCopy] : []),
    ...(isPreview ? ['the plan a preview logs is the adoption proof'] : []),
  ].join(' · ');
}

/**
 * One updater run in full: what it moved between, how each phase went, the
 * inputs it replayed, and the output it captured, where the surface stores it.
 */
export function UpdaterRunDrawer<P extends LogPage & { transcript?: UpdateRunTranscriptMeta }>({
  source,
  runId,
  onClose,
}: {
  readonly source: UpdaterRunSource<P>;
  readonly runId: string | null;
  readonly onClose: () => void;
}): React.JSX.Element {
  const query = source.useRun(runId);
  const run = query.run;
  const settled = !query.isLoading && !query.notFound && !query.isError && run !== null;
  const transcript = run?.transcript;

  const title = (
    <div className="min-w-0">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
        Update run
      </span>
      <p className="mt-0.5 truncate text-sm font-semibold text-foreground">
        {run !== null ? (run.target_version ?? 'no target release') : ' '}
      </p>
    </div>
  );

  return (
    <LogDrawer
      open={runId !== null}
      onClose={onClose}
      storageKey="gl-updater-run-detail-width"
      title={title}
      srDescription="Updater run detail"
      testId="updater-run-detail"
      log={
        settled && run !== null && transcript !== undefined && source.log
          ? {
              recordId: run.id,
              heading: 'Transcript',
              source: source.log(run),
              hasLog: transcript.chunk_count > 0,
              emptyCopy: source.transcriptCopy?.empty(run) ?? emptyTranscriptCopy(run.outcome),
              // The page's own metadata is the fresher of the two: a run still
              // writing chunks grows between the detail read and this one.
              caption: (page: P | null) =>
                transcriptCaption(
                  page?.transcript ?? transcript,
                  run.mode === 'canary',
                  source.transcriptCopy?.truncated,
                ),
              downloadName: `updater-run-${run.id}.log`,
              label: 'Run transcript',
              noun: 'transcript',
              testId: 'updater-run-transcript',
              emptyTestId: 'updater-run-no-transcript',
            }
          : null
      }
    >
      {query.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : query.notFound ? (
        <Alert>
          <AlertDescription>
            This run is no longer stored. Run records are kept for 90 days and transcripts for the
            newest 30 runs.
          </AlertDescription>
        </Alert>
      ) : query.isError || run === null ? (
        <Alert variant="destructive">
          <AlertDescription>Couldn&rsquo;t load this run. Retry in a moment.</AlertDescription>
        </Alert>
      ) : (
        <DetailBody run={run} isFetching={query.isFetching} onRefresh={query.refetch} />
      )}
    </LogDrawer>
  );
}
